import './styles.scss';

import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import {
  TrainingReportPdfData,
  parseTrainingReportCities,
  parseTrainingReportPdfParams,
} from '@/features/PdfLayout/layouts/TrainingReport/utils';

import { ReportColumns } from '@/pages/Reports/DynamicReport/components/ReportTable/utils';
import SkyhopPdfHeader from '@/features/PdfLayout/components/SkyhopPdfHeader';
import TrainingReportPdfFooter from '@/features/PdfLayout/layouts/TrainingReport/TrainingReportPdfFooter';
import TrainingReportPdfSummary from '@/features/PdfLayout/layouts/TrainingReport/TrainingReportPdfSummary';
import TrainingReportPdfTable from '@/features/PdfLayout/layouts/TrainingReport/TrainingReportPdfTable';
import { parseObj } from '@/utils';
import reportQuery from '@/api/services/reports/reportQuery';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { usePdfLayout } from '@/features/PdfLayout';
import { useSearchParams } from 'react-router-dom';

const TrainingReportPdf = (): ReactElement => {
  useDocumentTitle('Training Checklist Report');
  const [error, data] = useTrainingReportPdfData();

  if (error) return <div className="TrainingReportPdf-Error">Error loading training report.</div>;
  return (
    <div className="TrainingReportPdf">
      <SkyhopPdfHeader>
        Training Checklist
        <br />
        Report
      </SkyhopPdfHeader>
      <div className="TrainingReportPdf-Content">
        {data && (
          <>
            <TrainingReportPdfSummary />
            <TrainingReportPdfTable />
          </>
        )}
      </div>
      <TrainingReportPdfFooter />
    </div>
  );
};

const useTrainingReportPdfData = (): [unknown, TrainingReportPdfData | null] => {
  const [searchParams] = useSearchParams();
  const params = useMemo(() => parseTrainingReportPdfParams(searchParams), [searchParams]);
  const [error, data, setState] = usePdfLayout(({ state: { error, data }, setState }) => [
    error,
    data as TrainingReportPdfData | null,
    setState,
  ]);

  const fetchTrainingReport = useCallback(
    async (isCancelled: () => boolean): Promise<void> => {
      setState((current) => ({ ...current, loading: true, data: null, error: null }));
      try {
        // reportQuery mutates its input, so hand it a copy
        const res = await reportQuery('trainingReport')({ ...params.input });
        if (isCancelled()) return;
        // 1002 is the "no results" code, which DynamicReport also treats as a non-error
        if (res?.error && res.error?.extensions?.code !== 1002) throw res.error;
        const { header = {}, tables = [] } = parseObj(res?.output || {}) || {};
        const [table] = tables;
        const columns: ReportColumns = table?.metadata?.columns || {};
        // Set data and loading together so PdfReady never shows before the data renders
        setState((current) => ({
          ...current,
          loading: false,
          data: { params, header, columns, cities: parseTrainingReportCities(table?.children, columns) },
        }));
      } catch (err) {
        if (isCancelled()) return;
        setState((current) => ({ ...current, loading: false, data: null, error: err || 'Failed to load training report' }));
      }
    },
    [params, setState]
  );

  useEffect(() => {
    if (params.missing.length) {
      setState((current) => ({ ...current, loading: false, data: null, error: `Missing required params: ${params.missing.join(', ')}` }));
      return;
    }
    let cancelled = false;
    fetchTrainingReport(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [params, setState, fetchTrainingReport]);

  return [error, data];
};

export default TrainingReportPdf;
